import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { experienciasApi } from '../api/experiencias'
import Boton from '../components/Boton'
import HistoriasExperiencia from '../components/HistoriasExperiencia'
import CampoDireccion from '../components/CampoDireccion'
import InvitacionExperiencia from '../components/InvitacionExperiencia'
import { colors, radius, space, sx, text } from '../design/tokens'
import { PROVINCIAS } from '../data/argentina'
import { isoToFecha } from '../utils/fechas'

/**
 * Alta y edición de una EXPERIENCIA de la tienda web.
 *
 * El contenido se carga en BLOQUES (relato, datos, qué incluye, programa) y no
 * como HTML: así la pieza se dibuja siempre con la estética Ligier y anda bien
 * en el celular sin que nadie tenga que ocuparse.
 *
 * "Duplicar de" es el reemplazo del duplicar de Magento: trae el armado entero
 * de una experiencia anterior y solo queda cambiar fecha, título y foto.
 */

const VACIA = {
  tipo: '', titulo: '', lugar: '', fecha_evento: '', cuando_texto: '',
  publicar_desde: '', publicar_hasta: '', precio: '', precio_texto: '',
  vende_online: true, cupos: 24, turnos: [], resumen: '', resumen_ancho: '',
  imagen: '', imagen_color: false, imagenes: [], imagen_compartir: '', relato: [''], datos: [], incluye: [],
  programa: [], nota: '', activo: true, oculta: false,
  // Google Ads (Martin, 30 sep 2026): su propia campaña, con fin el día del
  // evento y la zona de acá. Tocar cualquiera de estos desaprueba la campaña.
  ads_promocionar: false, ads_presupuesto_diario: 5000, ads_provincia: '', ads_direccion: '',
  ads_lat: null, ads_lng: null, ads_radio_km: '', ads_keywords: '',
}
const CAMPOS_ADS = [
  'ads_promocionar', 'ads_presupuesto_diario', 'ads_provincia', 'ads_direccion',
  'ads_lat', 'ads_lng', 'ads_radio_km', 'ads_keywords',
]
/** Igualdad "como la guarda la base": 5000 y "5000.00", -34.5 y "-34.5000000", '' y null. */
const mismoValorAds = (a, b) => {
  const v = (x) => (x === '' || x === undefined ? null : x)
  const [x, y] = [v(a), v(b)]
  if (x === null || y === null) return x === y
  if (typeof x === 'boolean' || typeof y === 'boolean') return Boolean(x) === Boolean(y)
  const [nx, ny] = [Number(x), Number(y)]
  if (!Number.isNaN(nx) && !Number.isNaN(ny) && String(x).trim() !== '' && String(y).trim() !== '') return nx === ny
  return String(x) === String(y)
}

/**
 * La PLANTILLA DE CENA (Martin, 28 ago 2026: "que cualquier nueva cena se vea
 * igual que Pop Up en Sarasa Negro"). Es la estructura que la web dibuja como
 * esa ficha: dos datos (fecha y lugar), dos "incluye", el programa en dos
 * bloques —"Los vinos" (uno por línea, el año al final) y "El menú" (bloques
 * "Encabezado / plato" separados por una línea en blanco)— y los dos turnos.
 * El texto lo escribe el operador; acá solo va el molde.
 */
const PLANTILLA_CENA = {
  datos: [['Fecha', ''], ['Lugar', '']],
  incluye: ['Menú de pasos de …', 'Selección de vinos de Vinos Guardados'],
  programa: [
    { titulo: 'Los vinos', texto: '' },
    { titulo: 'El menú', texto: 'Entrada\n\n\nPaso 1\n\n\nPaso 2\n\n\nPostre\n' },
  ],
  turnos: ['21:00', '21:30'],
}
const PLACEHOLDER_PROGRAMA = {
  vinos: 'Etchart Torrontés 1992\nSaint Felicien Malbec 1988\nBressia Profundo 2003',
  menu: 'Entrada\nSando de atún rojo\n\nPaso 1\nVieiras, buttermilk, salsa verde\n\nPostre\nVolcán de chocolate',
  jornada: 'Día 1 · Viña Cobos y cena de enólogos\n(un párrafo por jornada)',
}
const sinArmado = (f) => f.datos.length === 0 && f.incluye.length === 0 && f.programa.length === 0

/**
 * El prompt ARMADO para la IA de cada uno (Martin, 1 sep 2026: "que
 * cualquiera lo pida en su IA y le traiga los campos del evento ya creados").
 * Se copia, se le pega abajo la info cruda del evento (flyer, mail, chat), y
 * lo que la IA devuelve se pega en "Pegar el texto" → Acomodar: viene con los
 * nombres de campo que el acomodador ya entiende.
 */
const PROMPT_IA_CENA = `Armame la ficha de esta cena para cargarla en la web. Devolveme SOLO el texto final con este formato exacto, un campo por renglón o sección. Lo que no sepas dejalo vacío — no inventes ningún dato.

Título: …
Fecha: … (ej: 25 de septiembre)
Lugar: …
Precio por lugar: … (solo el número)
Lugares: … (cupo total, solo el número)
Turnos: … (ej: 21:00 y 21:30)

Una línea: … (el gancho corto, una sola frase)
Presentación: … (dos o tres frases)

La noche:
(dos o tres párrafos: el lugar, la propuesta, las copas)

Los vinos:
(uno por línea, el nombre del vino con la cosecha al final, ej: Etchart Torrontés 1992)

El menú:
Entrada
(el plato)

Paso 1
(el plato)

Postre
(el plato)

La frase final: …

Esta es la información del evento:
[PEGÁ ACÁ TODO LO QUE TENGAS: flyer, mail, notas]`

/* ── Cena estructurada (Martin, 1 sep 2026, sobre la base de Estancia Vigil):
   el form ES la página en su orden — portada/compra, "La noche", fotos,
   "Los vinos" (filas cosecha+vino), "El menú" (filas paso+plato), la frase
   final. Los vinos y el menú se editan como filas pero se GUARDAN en el mismo
   formato de texto que la web ya dibuja (año al final de la línea / bloques
   "encabezado\nplato" separados por línea en blanco): una cena vieja se abre
   en filas y una nueva se ve igual que la referencia. */
const esBloqueVinos = (t) => /vino/i.test(t || '')
const esBloqueMenu = (t) => /men[uú]/i.test(t || '')

/** "Etchart Torrontés 1992" → { cosecha: '1992', nombre: 'Etchart Torrontés' }.
 *  Varias cosechas (Martin, 5 oct 2026): "Weinert Cavas 1977 · 1983" →
 *  { cosecha: '1977 · 1983', … }. Misma regex que vinoDeLinea en la tienda. */
function vinosDesdePrograma(programa) {
  const b = (programa || []).find((p) => esBloqueVinos(p.titulo))
  if (!b || !b.texto) return []
  return b.texto.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
    const m = l.match(/^(.*?)[\s,·-]*((?:19|20)\d{2}(?:\s*(?:[,·\/&–-]|y(?=\s))?\s*(?:19|20)\d{2})*)\s*$/)
    return m && m[1].trim() ? { cosecha: m[2], nombre: m[1].trim() } : { cosecha: '', nombre: l }
  })
}

function menuDesdePrograma(programa) {
  const b = (programa || []).find((p) => !esBloqueVinos(p.titulo) && esBloqueMenu(p.titulo))
  if (!b || !b.texto) return []
  return b.texto.split(/\n\s*\n/)
    .map((g) => g.split('\n').map((x) => x.trim()).filter(Boolean))
    .filter((g) => g.length > 0)
    .map((g) => ({ paso: g[0], plato: g.slice(1).join('\n') }))
}

const MOLDE_MENU = [
  { paso: 'Entrada', plato: '' }, { paso: 'Paso 1', plato: '' },
  { paso: 'Paso 2', plato: '' }, { paso: 'Postre', plato: '' },
]

/** Serializa las filas al formato de texto del programa. Los bloques que no
 *  son ni vinos ni menú (raros en una cena) se conservan tal cual. */
function programaDeCena(vinos, menu, programaPrevio) {
  const bloques = []
  const vinosTxt = (vinos || [])
    .filter((v) => (v.nombre || '').trim())
    .map((v) => [v.nombre.trim(), (v.cosecha || '').trim()].filter(Boolean).join(' '))
    .join('\n')
  if (vinosTxt) bloques.push({ titulo: 'Los vinos', texto: vinosTxt })
  const menuTxt = (menu || [])
    .filter((m) => (m.paso || '').trim() && (m.plato || '').trim())
    .map((m) => `${m.paso.trim()}\n${m.plato.trim()}`)
    .join('\n\n')
  if (menuTxt) bloques.push({ titulo: 'El menú', texto: menuTxt })
  const otros = (programaPrevio || []).filter((p) => !esBloqueVinos(p.titulo) && !esBloqueMenu(p.titulo))
  return [...bloques, ...otros]
}

/** Los campos que se copian al duplicar: el armado, no la fecha ni el artículo. */
const CAMPOS_DUPLICABLES = [
  'tipo', 'lugar', 'precio', 'precio_texto', 'vende_online', 'cupos', 'turnos',
  'resumen', 'resumen_ancho', 'imagen', 'imagen_color', 'imagenes',
  'imagen_compartir', 'relato', 'datos', 'incluye', 'programa', 'nota',
]

export default function ExperienciaWebFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const esNueva = !id

  const [form, setForm] = useState(VACIA)
  const [tipos, setTipos] = useState([])
  const [otras, setOtras] = useState([])
  // "Duplicar de" solo ofrece las armadas con la plantilla NUEVA (regla del
  // backend, flag plantilla_nueva): copiar una vieja arrastra el formato viejo.
  const duplicables = otras.filter((o) => o.plantilla_nueva && !o.archivada)
  const [urlPublica, setUrlPublica] = useState('')
  const [articulo, setArticulo] = useState(null)
  // Lo que el panel sabe de Google Ads tal como está GUARDADO (aprobación, faltantes, estado de la campaña).
  const [ads, setAds] = useState(null)
  const [aprobandoAds, setAprobandoAds] = useState(false)
  const [proximoSku, setProximoSku] = useState('')
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')
  // Sube al guardar: las historias se vuelven a pedir con los datos nuevos.
  const [versionHistorias, setVersionHistorias] = useState(0)
  const fotoRef = useRef(null)
  const fotoCompartirRef = useRef(null)

  // "Pegar el texto": el borrador tal cual y lo que devolvió el repartidor.
  const [pegado, setPegado] = useState('')
  const [acomodando, setAcomodando] = useState(false)
  const [advertencias, setAdvertencias] = useState([])

  const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }))

  // Filas de la cena (vinos y menú). Estado propio y no una vista derivada:
  // una fila recién agregada está vacía y la serialización la descartaría en
  // cada render. Se sincronizan desde `programa` al cargar/copiar/acomodar y
  // se serializan al guardar.
  const [vinos, setVinos] = useState([])
  const [menuCena, setMenuCena] = useState([])
  const sincronizarCena = (programa) => {
    const v = vinosDesdePrograma(programa)
    const m = menuDesdePrograma(programa)
    setVinos(v.length ? v : [{ cosecha: '', nombre: '' }])
    setMenuCena(m.length ? m : MOLDE_MENU)
  }

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      const { data } = await experienciasApi.listar()
      setTipos(data.tipos)
      setOtras(data.experiencias)
      setProximoSku(data.proximo_sku)

      if (esNueva) {
        // Una cena nueva arranca con la plantilla de cenas puesta (la web la
        // dibuja igual que la referencia); el operador solo completa el texto.
        setForm((f) => {
          const tipo = f.tipo || data.tipos[0]?.slug || ''
          return tipo === 'cenas' && sinArmado(f) ? { ...f, tipo, ...PLANTILLA_CENA } : { ...f, tipo }
        })
        sincronizarCena(PLANTILLA_CENA.programa)
      } else {
        const { data: exp } = await experienciasApi.ver(id)
        setArticulo(exp.articulo)
        setAds(exp)
        setUrlPublica(exp.url_publica || '')
        setForm({
          ...VACIA,
          ...Object.fromEntries(
            Object.keys(VACIA).map((k) => [k, exp[k] ?? VACIA[k]]),
          ),
          // Las fechas llegan como timestamp y el input necesita YYYY-MM-DD.
          fecha_evento: (exp.fecha_evento || '').slice(0, 10),
          publicar_desde: (exp.publicar_desde || '').slice(0, 10),
          publicar_hasta: (exp.publicar_hasta || '').slice(0, 10),
          relato: exp.relato?.length ? exp.relato : [''],
        })
        sincronizarCena(exp.programa)
      }
    } catch {
      setError('No se pudo cargar la experiencia.')
    } finally {
      setCargando(false)
    }
  }, [id, esNueva])
  useEffect(() => { cargar() }, [cargar])

  /** Trae el armado de otra experiencia (todo menos fecha, título y artículo). */
  const copiarDe = (origenId) => {
    const origen = otras.find((o) => String(o.id) === String(origenId))
    if (!origen) return
    setForm((f) => ({
      ...f,
      ...Object.fromEntries(CAMPOS_DUPLICABLES.map((k) => [k, origen[k] ?? VACIA[k]])),
      relato: origen.relato?.length ? origen.relato : [''],
    }))
    sincronizarCena(origen.programa)
  }

  /**
   * Pega el texto del evento —como esté escrito— y lo reparte en los bloques.
   * Solo COMPLETA el formulario: no guarda nada y no pisa lo que ya tenga
   * cargado. Lo que se publica es lo que quede acá después de revisarlo.
   */
  const acomodarTexto = async () => {
    if (!pegado.trim()) return
    setAcomodando(true)
    setError('')
    try {
      const { data } = await experienciasApi.interpretar(pegado)
      // Manda lo que trajo el texto; lo que el texto no diga queda como está
      // (el backend descarta los campos vacíos, así que nada se borra solo).
      setForm((f) => ({ ...f, ...(data.campos || {}) }))
      if (data.campos?.programa) sincronizarCena(data.campos.programa)
      setAdvertencias(data.advertencias || [])
    } catch {
      setError('No se pudo acomodar el texto.')
    } finally {
      setAcomodando(false)
    }
  }

  /**
   * Sube una o varias fotos. La primera de la experiencia queda de portada;
   * las siguientes se suman a la galería (la ficha web las muestra todas).
   */
  const subirFotoCompartir = async (file) => {
    if (!file) return
    try {
      const { data } = await experienciasApi.subirFoto(file)
      set('imagen_compartir', data.url)
    } catch {
      setError('No se pudo subir la imagen para compartir.')
    }
  }

  const subirFotos = async (files) => {
    if (!files?.length) return
    setError('')
    for (const file of Array.from(files)) {
      try {
        const { data } = await experienciasApi.subirFoto(file)
        setForm((f) => f.imagen
          ? { ...f, imagenes: [...f.imagenes, data.url] }
          : { ...f, imagen: data.url })
      } catch (e) {
        // El motivo real (formato no soportado, archivo muy pesado) viene del
        // backend — sin mostrarlo, todo fallo parece el mismo misterio.
        setError(e.response?.data?.message || 'No se pudo subir la foto.')
        break
      }
    }
  }

  /** Quitar la portada asciende a la primera de la galería, si hay. */
  const quitarPortada = () =>
    setForm((f) => ({ ...f, imagen: f.imagenes[0] || '', imagenes: f.imagenes.slice(1) }))

  /** La elegida pasa a portada y la portada anterior encabeza la galería. */
  const hacerPortada = (i) =>
    setForm((f) => ({
      ...f,
      imagen: f.imagenes[i],
      imagenes: [f.imagen, ...f.imagenes.filter((_, j) => j !== i)].filter(Boolean),
    }))

  // La venta se corta antes del evento: a veces es a propósito (una cena que
  // cierra lista días antes), pero con el estacionamiento del LEX #8 fue un
  // descuido y el día del evento nadie pudo comprar. Se avisa, no se bloquea.
  const cierraAntesDelEvento =
    form.vende_online && form.activo && form.fecha_evento && form.publicar_hasta &&
    form.publicar_hasta < form.fecha_evento

  const guardar = async () => {
    if (cierraAntesDelEvento && !confirm(
      `La venta online se corta el ${isoToFecha(form.publicar_hasta)}, pero el evento es el ` +
      `${isoToFecha(form.fecha_evento)}: desde ese corte nadie va a poder comprar, ni siquiera ` +
      'el día del evento. ¿Cortar la venta antes es a propósito?'
    )) return
    setGuardando(true)
    setError('')
    try {
      const payload = {
        ...form,
        precio: form.precio === '' ? null : Number(form.precio),
        fecha_evento: form.fecha_evento || null,
        publicar_desde: form.publicar_desde || null,
        publicar_hasta: form.publicar_hasta || null,
        ads_presupuesto_diario: Number(form.ads_presupuesto_diario) || 0,
        ads_provincia: form.ads_provincia || null,
        ads_radio_km: form.ads_radio_km === '' || form.ads_radio_km === null ? null : Number(form.ads_radio_km),
        // Un párrafo vacío al final es ruido de edición, no contenido.
        relato: form.relato.filter((p) => p.trim() !== ''),
        datos: form.datos.filter(([k]) => k.trim() !== ''),
        incluye: form.incluye.filter((i) => i.trim() !== ''),
        // En la cena, los vinos y el menú viajan desde las filas estructuradas
        // (mismo formato de texto que siempre — la web no cambia).
        programa: (form.tipo === 'cenas'
          ? programaDeCena(vinos, menuCena, form.programa)
          : form.programa
        ).filter((p) => p.titulo.trim() !== ''),
      }
      const { data } = esNueva
        ? await experienciasApi.crear(payload)
        : await experienciasApi.actualizar(id, payload)
      navigate(`/web/experiencias/${data.id}`, { replace: true })
      if (!esNueva) cargar()
      setVersionHistorias((v) => v + 1)
    } catch (e) {
      const errores = e?.response?.data?.errors
      setError(errores ? Object.values(errores).flat().join(' · ') : 'No se pudo guardar.')
    } finally {
      setGuardando(false)
    }
  }

  // La aprobación vale para lo GUARDADO: con cambios de Ads sin guardar no se
  // aprueba (se aprobaría otra cosa que la que el operador ve en pantalla).
  const adsSinGuardar = !!ads && CAMPOS_ADS.some((k) => !mismoValorAds(form[k], ads[k]))

  const cambiarAprobacionAds = async (aprobar) => {
    if (!aprobar && !confirm('¿Retirar la aprobación? La campaña de Google se pausa en la próxima sincronización.')) return
    setAprobandoAds(true)
    setError('')
    try {
      const { data } = aprobar
        ? await experienciasApi.aprobarAds(id)
        : await experienciasApi.desaprobarAds(id)
      setAds(data)
    } catch (e) {
      const faltan = e?.response?.data?.faltan
      setError(faltan ? `Google Ads: ${faltan.join(' ')}` : 'No se pudo cambiar la aprobación de Google Ads.')
    } finally {
      setAprobandoAds(false)
    }
  }

  const eliminar = async () => {
    if (!confirm('¿Sacar esta experiencia de la web? El artículo y sus ventas no se tocan.')) return
    await experienciasApi.eliminar(id)
    navigate('/web/experiencias')
  }

  if (cargando) return <p style={{ color: colors.textMeta }}>Cargando…</p>

  return (
    <div style={{ maxWidth: 880 }}>
      <div style={sx.toolbar}>
        <Boton variante="ghost" onClick={() => navigate(-1)}>← Volver</Boton>
        <h2 style={{ margin: 0, fontSize: text.title, color: colors.text }}>
          {esNueva ? 'Nueva experiencia' : form.titulo || 'Experiencia'}
        </h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: space.md }}>
          {!esNueva && <Boton variante="destructive" onClick={eliminar}>Sacar de la web</Boton>}
          <Boton variante="primary" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Boton>
        </div>
      </div>

      {error && <p style={estilos.error}>{error}</p>}

      {/* ── Pegar el texto ───────────────────────────────────────────
          El evento ya está escrito en algún lado (un mail, un documento, un
          chat). Se pega tal cual y cae repartido en los bloques de abajo. */}
      <Card
        titulo="Pegar el texto"
        accion={
          // Secundario a propósito: la acción principal de la pantalla es
          // Guardar, y esto es la ayuda para llegar.
          <Boton variante="secondary" sm onClick={acomodarTexto} disabled={acomodando || !pegado.trim()}>
            {acomodando ? 'Acomodando…' : 'Acomodar el texto'}
          </Boton>
        }
      >
        <p style={{ ...estilos.ayuda, marginTop: 0, marginBottom: space.md }}>
          Pegá el evento como esté escrito y se reparte solo en los bloques de
          abajo: el relato, los datos, qué incluye y el programa. Lo que el texto
          no diga queda como está — y nada se publica hasta que lo revises y
          guardes. El estilo lo pone la web.
        </p>
        <p style={{ ...estilos.ayuda, marginTop: 0, marginBottom: space.md }}>
          ¿La info está desordenada (flyer, mail, chat)?{' '}
          <BotonCopiarPrompt />{' '}
          — se lo das a tu IA (ChatGPT, Claude, la que uses) junto con lo que
          tengas del evento, y lo que te devuelva lo pegás acá: viene con los
          campos ya armados.
        </p>
        <textarea
          data-nocaps
          style={{ ...estilos.area, minHeight: 160 }}
          value={pegado}
          onChange={(e) => setPegado(e.target.value)}
          placeholder={'Cena Botellones — jueves 27 de agosto, Casa VG\n$1.000.000 por persona, mesa de 24\n\nTodo comienza en la cava, con una copa en la mano…\n\nQué incluye:\n- Recorrido por la cava\n- Menú de cinco pasos'}
        />
        {advertencias.length > 0 && (
          <div style={estilos.avisoInline}>
            <strong>Revisá esto antes de guardar:</strong>
            <ul style={{ margin: `${space.sm}px 0 0`, paddingLeft: space['2xl'] }}>
              {advertencias.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        )}
      </Card>

      {/* ── Duplicar de otra ─────────────────────────────────────── */}
      {esNueva && duplicables.length > 0 && (
        <Card titulo="Duplicar de">
          <p style={estilos.ayuda}>
            Elegí una experiencia anterior y se copia todo el armado: relato, datos,
            qué incluye, programa y foto. La fecha y el artículo arrancan en blanco
            porque siempre cambian.
          </p>
          <select
            style={{ ...sx.input, minWidth: 320 }}
            defaultValue=""
            onChange={(e) => copiarDe(e.target.value)}
          >
            <option value="">— Empezar de cero —</option>
            {duplicables.map((o) => (
              <option key={o.id} value={o.id}>{o.titulo}</option>
            ))}
          </select>
        </Card>
      )}

      {/* ── Lo básico ────────────────────────────────────────────── */}
      <Card titulo="Lo básico">
        <div style={estilos.grid}>
          <Campo label="Sección">
            <select style={sx.input} value={form.tipo} onChange={(e) => set('tipo', e.target.value)}>
              {tipos.map((t) => <option key={t.slug} value={t.slug}>{t.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Título" ancho>
            <input data-nocaps style={{ ...sx.input, width: '100%' }} value={form.titulo}
              onChange={(e) => set('titulo', e.target.value)} placeholder="Especial Caviar" />
          </Campo>
          <Campo label="Lugar">
            <input data-nocaps style={sx.input} value={form.lugar || ''}
              onChange={(e) => set('lugar', e.target.value)} placeholder="Casa VG" />
          </Campo>
          <Campo label="Fecha del evento">
            <input data-nocaps type="date" style={sx.input} value={form.fecha_evento || ''}
              onChange={(e) => set('fecha_evento', e.target.value)} />
          </Campo>
          <Campo label="Cómo se escribe la fecha" ancho>
            <input data-nocaps style={{ ...sx.input, width: '100%' }} value={form.cuando_texto || ''}
              onChange={(e) => set('cuando_texto', e.target.value)}
              placeholder="Se arma sola desde la fecha — completá solo si va distinto" />
          </Campo>
        </div>
      </Card>

      {/* ── Precio y cupos ───────────────────────────────────────── */}
      <Card titulo="Precio y lugares">
        <div style={estilos.grid}>
          <Campo label="Precio por lugar">
            <input data-nocaps type="number" style={sx.input} value={form.precio ?? ''}
              onChange={(e) => set('precio', e.target.value)} placeholder="1200000" />
          </Campo>
          <Campo label="Lugares">
            <input data-nocaps type="number" style={sx.input} value={form.cupos}
              onChange={(e) => set('cupos', Number(e.target.value))} />
          </Campo>
          {/* Turnos (Martin, 26 ago 2026): "21:00, 21:30". Con turnos, la web
              obliga a elegir uno y el turno queda en el renglón de la NV —
              es con lo que Ventas arma las listas. Vacío = sin turnos. */}
          <Campo label="Turnos (separados por coma)">
            <input data-nocaps style={sx.input} placeholder="21:00, 21:30"
              value={(form.turnos || []).join(', ')}
              onChange={(e) => set('turnos', e.target.value.split(',').map((t) => t.trim()).filter(Boolean))} />
          </Campo>
          <Campo>
            <Check label="Se compra online" valor={form.vende_online}
              onChange={(v) => set('vende_online', v)} />
          </Campo>
          {!form.vende_online && (
            <Campo label="Precio como se muestra" ancho>
              <input data-nocaps style={{ ...sx.input, width: '100%' }} value={form.precio_texto || ''}
                onChange={(e) => set('precio_texto', e.target.value)}
                placeholder="USD 12.500 + IVA por persona" />
            </Campo>
          )}
        </div>
        <p style={estilos.ayuda}>
          Sin el tilde, la experiencia se publica igual pero el botón lleva a consultar
          en vez de al carrito.
        </p>
      </Card>

      {/* ── Publicación ──────────────────────────────────────────── */}
      <Card titulo="Cuándo se ve en la web">
        <div style={estilos.grid}>
          <Campo label="Desde">
            <input data-nocaps type="date" style={sx.input} value={form.publicar_desde || ''}
              onChange={(e) => set('publicar_desde', e.target.value)} />
          </Campo>
          <Campo label="Hasta">
            <input data-nocaps type="date" style={sx.input} value={form.publicar_hasta || ''}
              onChange={(e) => set('publicar_hasta', e.target.value)} />
          </Campo>
          <Campo>
            <Check label="Publicada" valor={form.activo} onChange={(v) => set('activo', v)} />
          </Campo>
          <Campo>
            <Check label="Oculta (solo por link)" valor={form.oculta}
              onChange={(v) => set('oculta', v)} />
          </Campo>
        </div>
        {/* El link público, a la vista (26 ago 2026: "tildé oculta solo por
            link pero no sé cuál es el link"). Y la regla que confundió:
            "Publicada" es lo que hace existir la página; "Oculta" solo la
            saca del listado y del buscador. Sin "Publicada" el link da
            "no encontrada", esté oculta o no. */}
        <div style={estilos.linkPublico}>
          <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
            <b>Link público:</b>
            {urlPublica ? (
              <>
                <a href={urlPublica} target="_blank" rel="noreferrer" style={{ color: colors.link, wordBreak: 'break-all' }}>{urlPublica}</a>
                <Boton variante="ghost" sm type="button"
                  onClick={() => navigator.clipboard?.writeText(urlPublica)}
                  title="Copiar el link para mandarlo por WhatsApp o mail">Copiar</Boton>
              </>
            ) : (
              <span style={{ color: colors.textMeta }}>se arma al guardar, con el título</span>
            )}
          </div>
          <div style={{ marginTop: space.xs, color: form.activo ? colors.textSec : colors.warn }}>
            {!form.activo
              ? '⚠ Sin "Publicada" el link NO funciona (la web dice "no encontrada"), aunque esté oculta: "Oculta" solo la saca del listado y del buscador.'
              : form.oculta
                ? 'Oculta: no aparece en la landing ni en el buscador — se llega solo por este link. Compartilo por WhatsApp o mail.'
                : 'Visible en la landing de experiencias y por este link.'}
          </div>
        </div>
        {cierraAntesDelEvento && (
          <p style={{
            margin: `${space.md}px 0 0`, padding: `${space.sm}px ${space.md}px`,
            background: colors.warnBg, color: colors.warn,
            borderRadius: radius.sm, fontSize: text.sec,
          }}>
            ⚠ La venta se corta el {isoToFecha(form.publicar_hasta)} y el evento es el{' '}
            {isoToFecha(form.fecha_evento)}: desde ese corte nadie va a poder comprar, ni
            siquiera el día del evento. Si no es a propósito, dejá &quot;Hasta&quot; vacío
            (se corta sola el día del evento).
          </p>
        )}
        <p style={estilos.ayuda}>
          Vacío arriba = se publica desde ya. Vacío abajo = se corta sola el día del
          evento. Terminada la fecha desaparece de la web sin que nadie tenga que
          acordarse. Oculta = no aparece en la página de Experiencias; se compra
          solo con el link directo de su ficha (para mandar por WhatsApp o mail).
        </p>
      </Card>

      {/* ── Fotos ────────────────────────────────────────────────────
          La primera es la PORTADA (la tarjeta de la landing); todas juntas son
          la galería de la ficha, como las fotos de cualquier producto. */}
      <Card titulo="Fotos" accion={
        <Boton variante="secondary" sm onClick={() => fotoRef.current?.click()}>
          + Agregar fotos
        </Boton>
      }>
        <input ref={fotoRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
          onChange={(e) => { subirFotos(e.target.files); e.target.value = '' }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.lg }}>
          {!form.imagen && (
            <div style={{ ...estilos.miniatura, ...estilos.sinFoto }}>sin fotos</div>
          )}
          {form.imagen && (
            <FotoMiniatura url={form.imagen} portada onQuitar={quitarPortada} />
          )}
          {form.imagenes.map((url, i) => (
            <FotoMiniatura key={`${url}-${i}`} url={url}
              onQuitar={() => set('imagenes', form.imagenes.filter((_, j) => j !== i))}
              onPortada={() => hacerPortada(i)} />
          ))}
        </div>
        {/* Imagen SOLO para el preview del link compartido (WhatsApp/redes).
            Vacía = se comparte la portada. No aparece en la página. */}
        <div style={{ marginTop: space.xl, paddingTop: space.lg, borderTop: `1px solid ${colors.borderSoft}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: space.md, marginBottom: space.sm }}>
            <span style={{ fontSize: text.sec, fontWeight: 600, color: colors.textSec }}>
              Imagen al compartir el link (WhatsApp)
            </span>
            <Boton variante="ghost" sm onClick={() => fotoCompartirRef.current?.click()}>
              {form.imagen_compartir ? 'Cambiar' : '+ Elegir imagen'}
            </Boton>
            <input ref={fotoCompartirRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { subirFotoCompartir(e.target.files?.[0]); e.target.value = '' }} />
          </div>
          {form.imagen_compartir ? (
            <FotoMiniatura url={form.imagen_compartir} onQuitar={() => set('imagen_compartir', '')} />
          ) : (
            <span style={estilos.ayuda}>
              Opcional — si está vacía, el preview se arma solo: la portada con el
              nombre del evento estampado. Si subís una pieza acá, se comparte TAL
              CUAL (sin estampar nada). Solo se ve al compartir; la página no cambia.
            </span>
          )}
        </div>
        <div style={{ display: 'grid', gap: space.sm, marginTop: space.lg }}>
          <Check label="Mostrarlas en color" valor={form.imagen_color}
            onChange={(v) => set('imagen_color', v)} />
          <span style={{ ...estilos.ayuda, marginTop: 0 }}>
            Por defecto las fotos van en blanco y negro, como el resto del sitio.
            La primera es la portada{form.tipo === 'cenas'
              ? ' (cuadrada, arriba junto a la compra); la segunda baja después del relato, al ancho de la columna.'
              : '; en la ficha se ven todas.'}
          </span>
        </div>
      </Card>

      {/* ── Resúmenes ────────────────────────────────────────────── */}
      <Card titulo="Cómo se presenta en la landing">
        <Campo label="Una línea (la tarjeta)" ancho>
          <textarea data-nocaps style={estilos.area} rows={2} value={form.resumen || ''}
            onChange={(e) => set('resumen', e.target.value)}
            placeholder="Champagnes, blancos y tres caviares con Pablo Pries." />
        </Campo>
        <Campo label="Dos o tres frases (cuando la sección tiene una sola)" ancho>
          <textarea data-nocaps style={estilos.area} rows={3} value={form.resumen_ancho || ''}
            onChange={(e) => set('resumen_ancho', e.target.value)} />
        </Campo>
      </Card>

      {/* ── El relato ("La noche" en la cena) ────────────────────── */}
      <Card titulo={form.tipo === 'cenas' ? '«La noche» — el relato' : 'El relato'} accion={
        <Boton variante="ghost" sm onClick={() => set('relato', [...form.relato, ''])}>+ Párrafo</Boton>
      }>
        {form.tipo === 'cenas' && (
          <p style={estilos.ayuda}>
            El primer párrafo sale GRANDE (la frase de apertura); los siguientes son el
            cuerpo — ahí van «la noche», «las copas», lo que quieras contar. La web le
            pone el título &quot;La noche&quot; sola.
          </p>
        )}
        {form.relato.map((parrafo, i) => (
          <Fila key={i} onQuitar={() => set('relato', form.relato.filter((_, j) => j !== i))}>
            <textarea data-nocaps style={estilos.area} rows={3} value={parrafo}
              onChange={(e) => set('relato', form.relato.map((p, j) => (j === i ? e.target.value : p)))} />
          </Fila>
        ))}
      </Card>

      {/* ── Los datos (solo viajes/otros: la plantilla de cenas no los
             dibuja — fecha y lugar ya van arriba del todo) ─────────── */}
      {form.tipo !== 'cenas' && (
      <Card titulo="Los datos" accion={
        <Boton variante="ghost" sm onClick={() => set('datos', [...form.datos, ['', '']])}>+ Dato</Boton>
      }>
        {form.datos.length === 0 && <p style={estilos.ayuda}>Cuándo, dónde, cuánta gente, quién cocina…</p>}
        {form.datos.map(([clave, valor], i) => (
          <Fila key={i} onQuitar={() => set('datos', form.datos.filter((_, j) => j !== i))}>
            <div style={{ display: 'flex', gap: space.md, width: '100%' }}>
              <input data-nocaps style={{ ...sx.input, width: 180 }} value={clave} placeholder="Mesa"
                onChange={(e) => set('datos', form.datos.map((d, j) => (j === i ? [e.target.value, d[1]] : d)))} />
              <input data-nocaps style={{ ...sx.input, flex: 1 }} value={valor} placeholder="Hasta 24 invitados"
                onChange={(e) => set('datos', form.datos.map((d, j) => (j === i ? [d[0], e.target.value] : d)))} />
            </div>
          </Fila>
        ))}
      </Card>
      )}

      {/* ── Qué incluye ──────────────────────────────────────────── */}
      <Card titulo="Qué incluye" accion={
        <Boton variante="ghost" sm onClick={() => set('incluye', [...form.incluye, ''])}>+ Ítem</Boton>
      }>
        {form.incluye.map((item, i) => (
          <Fila key={i} onQuitar={() => set('incluye', form.incluye.filter((_, j) => j !== i))}>
            <input data-nocaps style={{ ...sx.input, width: '100%' }} value={item}
              placeholder="Recorrido por la cava alta y la cava principal"
              onChange={(e) => set('incluye', form.incluye.map((x, j) => (j === i ? e.target.value : x)))} />
          </Fila>
        ))}
      </Card>

      {/* ── La cena, estructurada (Martin, 1 sep 2026): "Los vinos" y
             "El menú" como filas, en el orden de la página. Se guardan en el
             mismo formato de texto de siempre — la web no cambia. ──── */}
      {form.tipo === 'cenas' && (
        <>
          <Card titulo="Los vinos" accion={
            <Boton variante="ghost" sm onClick={() => setVinos([...vinos, { cosecha: '', nombre: '' }])}>+ Vino</Boton>
          }>
            <p style={estilos.ayuda}>
              En el orden en que se sirven. La web dibuja la COSECHA en verde arriba y el
              nombre abajo, uno por fila. Sin cosecha, va solo el nombre. Para varias
              cosechas del mismo vino, separalas con · o coma: 1977 · 1983 · 1994.
            </p>
            {vinos.map((v, i) => (
              <Fila key={i} onQuitar={() => setVinos(vinos.filter((_, j) => j !== i))}
                onSubir={i > 0 ? () => setVinos(movida(vinos, i, -1)) : null}
                onBajar={i < vinos.length - 1 ? () => setVinos(movida(vinos, i, 1)) : null}>
                <div style={{ display: 'flex', gap: space.md, width: '100%' }}>
                  <input data-nocaps style={{ ...sx.input, width: 190 }} value={v.cosecha} placeholder="1992 · 1995"
                    onChange={(e) => setVinos(vinos.map((x, j) => (j === i ? { ...x, cosecha: e.target.value } : x)))} />
                  <input data-nocaps style={{ ...sx.input, flex: 1 }} value={v.nombre} placeholder="Etchart Torrontés"
                    onChange={(e) => setVinos(vinos.map((x, j) => (j === i ? { ...x, nombre: e.target.value } : x)))} />
                </div>
              </Fila>
            ))}
          </Card>

          <Card titulo="El menú" accion={
            <Boton variante="ghost" sm onClick={() => setMenuCena([...menuCena, { paso: '', plato: '' }])}>+ Paso</Boton>
          }>
            <p style={estilos.ayuda}>
              Cada paso con su encabezado (Entrada, Paso 1, Postre…) y el plato. La web lo
              dibuja como la carta de un restaurante. Un paso sin plato no se publica.
            </p>
            {menuCena.map((m, i) => (
              <Fila key={i} onQuitar={() => setMenuCena(menuCena.filter((_, j) => j !== i))}
                onSubir={i > 0 ? () => setMenuCena(movida(menuCena, i, -1)) : null}
                onBajar={i < menuCena.length - 1 ? () => setMenuCena(movida(menuCena, i, 1)) : null}>
                <div style={{ display: 'flex', gap: space.md, width: '100%' }}>
                  <input data-nocaps style={{ ...sx.input, width: 140 }} value={m.paso} placeholder="Entrada"
                    onChange={(e) => setMenuCena(menuCena.map((x, j) => (j === i ? { ...x, paso: e.target.value } : x)))} />
                  <textarea data-nocaps style={{ ...estilos.area, flex: 1 }} rows={2} value={m.plato}
                    placeholder="Sando de atún rojo"
                    onChange={(e) => setMenuCena(menuCena.map((x, j) => (j === i ? { ...x, plato: e.target.value } : x)))} />
                </div>
              </Fila>
            ))}
          </Card>
        </>
      )}

      {/* ── El programa (viajes y otras secciones) ───────────────── */}
      {form.tipo !== 'cenas' && (
      <Card titulo="El programa" accion={
        <Boton variante="ghost" sm
          onClick={() => set('programa', [...form.programa, { titulo: '', texto: '' }])}>
          + Bloque
        </Boton>
      }>
        <p style={estilos.ayuda}>
          {form.tipo === 'viajes'
            ? 'Viaje: una jornada por bloque ("Día 1 · Viña Cobos…" y un párrafo). La web lo dibuja como itinerario.'
            : 'Bloques con título y texto; la web los dibuja como carta.'}
        </p>
        {form.programa.map((jornada, i) => (
          <Fila key={i} onQuitar={() => set('programa', form.programa.filter((_, j) => j !== i))}>
            <div style={{ display: 'grid', gap: space.md, width: '100%' }}>
              <input data-nocaps style={{ ...sx.input, width: '100%' }} value={jornada.titulo}
                placeholder={form.tipo === 'cenas' ? (i === 0 ? 'Los vinos' : 'El menú') : 'Día 1 · Viña Cobos y cena de enólogos'}
                onChange={(e) => set('programa', form.programa.map((p, j) => (j === i ? { ...p, titulo: e.target.value } : p)))} />
              <textarea data-nocaps style={estilos.area} rows={form.tipo === 'cenas' ? 6 : 3} value={jornada.texto || ''}
                placeholder={/vino/i.test(jornada.titulo) ? PLACEHOLDER_PROGRAMA.vinos : form.tipo === 'cenas' ? PLACEHOLDER_PROGRAMA.menu : PLACEHOLDER_PROGRAMA.jornada}
                onChange={(e) => set('programa', form.programa.map((p, j) => (j === i ? { ...p, texto: e.target.value } : p)))} />
            </div>
          </Fila>
        ))}
      </Card>
      )}

      {/* ── La nota del pie ──────────────────────────────────────── */}
      <Card titulo="La frase del final">
        <input data-nocaps style={{ ...sx.input, width: '100%' }} value={form.nota || ''}
          onChange={(e) => set('nota', e.target.value)}
          placeholder="Cada vino llega en su copa ideal. Ninguna se retira." />
      </Card>

      {/* ── Google Ads (su propia campaña, con la zona de acá) ───── */}
      <Card titulo="Google Ads">
        <Check label="Promocionar en Google" valor={form.ads_promocionar}
          onChange={(v) => set('ads_promocionar', v)} />
        {form.ads_promocionar && (
          <>
            <div style={{ ...estilos.grid, marginTop: space.lg }}>
              <Campo label="Presupuesto diario ($)">
                <input data-nocaps type="number" style={sx.input} value={form.ads_presupuesto_diario ?? ''}
                  onChange={(e) => set('ads_presupuesto_diario', e.target.value)} placeholder="5000" />
              </Campo>
              <Campo label="Provincia">
                <select style={sx.input} value={form.ads_provincia || ''}
                  onChange={(e) => set('ads_provincia', e.target.value)}>
                  <option value="">— Elegir —</option>
                  {PROVINCIAS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Campo>
              <Campo label="Radio alrededor del evento (km)">
                <input data-nocaps type="number" min="1" max="500" style={sx.input} value={form.ads_radio_km ?? ''}
                  onChange={(e) => set('ads_radio_km', e.target.value)} placeholder="30" />
              </Campo>
              <Campo label="Dirección del evento" ancho>
                {/* La ubicación sale SOLO de elegir una sugerencia: si se tipea
                    a mano, se borra la anterior (una dirección con el punto de
                    otra pautaría en el lugar equivocado). */}
                <CampoDireccion
                  value={form.ads_direccion || ''}
                  onChange={(v) => setForm((f) => ({ ...f, ads_direccion: v, ads_lat: null, ads_lng: null }))}
                  onPlaceSelected={(d) => setForm((f) => ({
                    ...f,
                    ads_direccion: [d.calle, d.localidad].filter(Boolean).join(', '),
                    ads_lat: d.lat ?? null,
                    ads_lng: d.lng ?? null,
                    ads_provincia: f.ads_provincia || d.provincia || '',
                  }))}
                  inputStyle={{ ...sx.input, width: '100%' }}
                  placeholder="Av. Callao 1111, CABA"
                />
              </Campo>
              <Campo label="Palabras clave extra (una por renglón)" ancho>
                <textarea data-nocaps rows={4} style={estilos.area} value={form.ads_keywords || ''}
                  onChange={(e) => set('ads_keywords', e.target.value)}
                  placeholder={'fabre montmayou\nhervé joyaux'} />
              </Campo>
            </div>
            <p style={estilos.ayuda}>
              Se pauta en toda la provincia más {form.ads_radio_km || '…'} km alrededor de la dirección,
              solo para gente que está ahí. La campaña termina sola el día del evento y se pausa
              sola si se agotan los lugares. Las palabras clave básicas (cena de vinos, maridaje, cata)
              las arma el sistema: acá van las propias del evento (bodega, chef, restaurante).
              {form.ads_direccion && (form.ads_lat === null || form.ads_lat === undefined) && (
                <strong style={{ color: colors.warn }}> Elegí la dirección de la lista de sugerencias: así queda la ubicación.</strong>
              )}
            </p>
          </>
        )}

        {esNueva ? (
          (form.ads_promocionar && <p style={estilos.ayuda}>Guardá la experiencia para poder aprobar la campaña.</p>)
        ) : ads && (ads.ads_promocionar || ads.ads_campaign_id) && (
          <div style={{ ...estilos.linkPublico, marginTop: space.lg }}>
            {ads.ads_aprobada_at ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
                <span style={{ color: colors.ok }}>
                  ✓ Aprobada{ads.ads_aprobada_por_nombre ? ` por ${ads.ads_aprobada_por_nombre}` : ''}
                  {' '}el {isoToFecha(ads.ads_aprobada_at.slice(0, 10))}
                </span>
                <Boton onClick={() => cambiarAprobacionAds(false)} disabled={aprobandoAds}>
                  Retirar aprobación
                </Boton>
              </div>
            ) : ads.ads_faltantes?.length ? (
              <>
                <strong>Para aprobar falta:</strong>
                <ul style={{ margin: `${space.xs}px 0 0`, paddingLeft: 20 }}>
                  {ads.ads_faltantes.map((f) => <li key={f}>{f}</li>)}
                </ul>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
                <Boton variante="primary" onClick={() => cambiarAprobacionAds(true)} disabled={aprobandoAds || adsSinGuardar}>
                  Aprobar para Google Ads
                </Boton>
                <span style={{ fontSize: text.sec, color: colors.textSec }}>
                  {adsSinGuardar
                    ? 'Hay cambios sin guardar: guardá primero y después aprobá.'
                    : `Autoriza a crear la campaña con $${Number(ads.ads_presupuesto_diario).toLocaleString('es-AR')} por día.`}
                </span>
              </div>
            )}
            {adsSinGuardar && ads.ads_aprobada_at && (
              <p style={{ ...estilos.avisoInline, marginTop: space.md }}>
                Cambiaste la configuración de Google Ads: al guardar vuelve a quedar sin aprobar.
              </p>
            )}
            {ads.ads_estado && (
              <p style={{ ...estilos.ayuda, marginTop: space.md }}>
                Campaña: <strong>{ads.ads_estado}</strong>
                {ads.ads_detalle ? ` · ${ads.ads_detalle}` : ''}
                {ads.ads_sync_at ? ` · última sincronización ${isoToFecha(ads.ads_sync_at.slice(0, 10))} ${ads.ads_sync_at.slice(11, 16)}` : ''}
              </p>
            )}
          </div>
        )}
      </Card>

      {/* ── El artículo (lo que hace que se pueda facturar) ──────── */}
      <Card titulo="Con qué se factura">
        {articulo ? (
          <>
            <p style={{ margin: 0, fontSize: text.body }}>
              <span style={sx.mono}>{articulo.codigo_alfa}</span>
              {' · '}{articulo.descripcion}
            </p>
            {articulo.hermano ? (
              <p style={{ ...estilos.ayuda, color: articulo.hermano.codigo_presea ? colors.ok : colors.warn }}>
                Hermanado con <span style={sx.mono}>{articulo.hermano.codigo_alfa}</span>
                {' · '}{articulo.hermano.descripcion}
                {articulo.hermano.codigo_presea
                  ? ` (Presea ${articulo.hermano.codigo_presea}). La factura sale con ese código.`
                  : '. Ese artículo todavía no tiene código de Presea: la factura va a frenar.'}{' '}
                <Link to={`/articulos/${articulo.id}`} style={{ color: colors.link }}>
                  Ver el vínculo →
                </Link>
              </p>
            ) : articulo.codigo_presea ? (
              <p style={{ ...estilos.ayuda, color: colors.ok }}>
                Presea lo conoce con el código {articulo.codigo_presea}. La factura sale sin frenos.
              </p>
            ) : (
              <p style={estilos.avisoInline}>
                Presea todavía no lo conoce. Se puede vender, pero la factura va a frenar
                hasta que el evento esté dado de alta allá.{' '}
                <Link to={`/articulos/${articulo.id}`} style={{ color: colors.link }}>
                  Abrir el artículo para vincularlo →
                </Link>
              </p>
            )}
          </>
        ) : (
          <p style={estilos.ayuda}>
            {form.vende_online
              ? `Al guardar se crea el artículo ${proximoSku} con este precio.`
              : 'Esta experiencia no se compra online, así que no necesita artículo todavía.'}
          </p>
        )}
      </Card>

      {/* ── Historias de Instagram (A · B · C, listas para descargar) ── */}
      {!esNueva && <HistoriasExperiencia key={versionHistorias} experienciaId={id} slug={urlPublica.split('/').pop()} />}

      {/* ── Invitaciones (la tarjeta + quienes compraron) ─────────── */}
      {!esNueva && <InvitacionExperiencia experienciaId={id} urlPublica={urlPublica} titulo={form.titulo} />}
    </div>
  )
}

/* ── Piezas de la pantalla ─────────────────────────────────────── */

/** "Copiar el prompt" con confirmación efímera — vive en la ayuda de Pegar el texto. */
function BotonCopiarPrompt() {
  const [copiado, setCopiado] = useState(false)
  return (
    <button
      type="button"
      style={{ border: 'none', background: 'none', padding: 0, color: colors.link, cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(PROMPT_IA_CENA)
          setCopiado(true)
          setTimeout(() => setCopiado(false), 2500)
        } catch { window.prompt('Copialo a mano:', PROMPT_IA_CENA) }
      }}
    >{copiado ? '✓ Prompt copiado' : 'Copiá este prompt'}</button>
  )
}

function Card({ titulo, accion, children }) {
  return (
    <section style={{ ...sx.card, marginBottom: space.xl }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: space.lg }}>
        <h3 style={sx.cardTitle}>{titulo}</h3>
        {accion && <div style={{ marginLeft: 'auto' }}>{accion}</div>}
      </div>
      {children}
    </section>
  )
}

/**
 * Un campo del formulario. Sin `label` (caso del tilde, que se explica solo) el
 * control se alinea con el pie de los de al lado en vez de subirse al renglón
 * del título.
 */
function Campo({ label, ancho, children }) {
  return (
    <div style={{
      display: 'grid', gap: space.xs, alignContent: label ? 'start' : 'end',
      gridColumn: ancho ? '1 / -1' : undefined,
    }}>
      {label && (
        <label style={{ fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </label>
      )}
      {children}
    </div>
  )
}

/** Un renglón de un bloque repetible, con su botón para quitarlo. */
function Fila({ onQuitar, onSubir, onBajar, children }) {
  return (
    <div style={{ display: 'flex', gap: space.md, alignItems: 'flex-start', marginBottom: space.md }}>
      <div style={{ flex: 1 }}>{children}</div>
      {(onSubir !== undefined || onBajar !== undefined) && (
        <>
          <button onClick={onSubir || undefined} disabled={!onSubir} title="Subir"
            style={{ ...estilos.quitar, ...(onSubir ? {} : estilos.moverApagado) }}>↑</button>
          <button onClick={onBajar || undefined} disabled={!onBajar} title="Bajar"
            style={{ ...estilos.quitar, ...(onBajar ? {} : estilos.moverApagado) }}>↓</button>
        </>
      )}
      <button onClick={onQuitar} title="Quitar" style={estilos.quitar}>×</button>
    </div>
  )
}

/** El elemento i corrido un lugar (d = ±1), para reordenar filas. */
function movida(arr, i, d) {
  const j = i + d
  const copia = [...arr]
  ;[copia[i], copia[j]] = [copia[j], copia[i]]
  return copia
}

/** Una foto de la galería, con sus acciones abajo. */
function FotoMiniatura({ url, portada, onQuitar, onPortada }) {
  return (
    <div style={{ display: 'grid', gap: space.xs, justifyItems: 'center' }}>
      <div style={{ position: 'relative' }}>
        <img src={url} alt="" style={estilos.miniatura} />
        {portada && <span style={estilos.badgePortada}>Portada</span>}
      </div>
      <div style={{ display: 'flex', gap: space.xs }}>
        {onPortada && (
          <button onClick={onPortada} title="Usar de portada" style={estilos.accionFoto}>
            ★ Portada
          </button>
        )}
        <button onClick={onQuitar} title="Quitar" style={estilos.accionFoto}>× Quitar</button>
      </div>
    </div>
  )
}

function Check({ label, valor, onChange }) {
  return (
    <label style={{ display: 'flex', gap: space.md, alignItems: 'center', fontSize: text.body, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!valor} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  )
}

const estilos = {
  grid: {
    display: 'grid', gap: space.lg,
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  },
  area: {
    ...sx.input, width: '100%', fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.6,
  },
  quitar: {
    background: 'transparent', border: `1px solid ${colors.border}`,
    borderRadius: radius.sm, color: colors.textMeta, cursor: 'pointer',
    fontSize: 16, lineHeight: 1, padding: '6px 10px',
  },
  moverApagado: { opacity: 0.35, cursor: 'default' },
  miniatura: {
    width: 140, height: 140, objectFit: 'cover',
    borderRadius: radius.md, border: `1px solid ${colors.border}`,
  },
  linkPublico: {
    marginTop: space.sm, padding: `${space.sm} ${space.md}`, borderRadius: radius.md,
    background: colors.bgSoft, border: `1px solid ${colors.border}`, fontSize: text.body,
  },
  sinFoto: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: colors.bgSoft, color: colors.textPlh, fontSize: text.sec,
  },
  badgePortada: {
    position: 'absolute', left: 6, top: 6, padding: '2px 8px',
    background: 'rgba(0,0,0,0.72)', color: '#fff', fontSize: 11,
    borderRadius: radius.sm, letterSpacing: '0.04em',
  },
  accionFoto: {
    background: 'transparent', border: `1px solid ${colors.border}`,
    borderRadius: radius.sm, color: colors.textSec, cursor: 'pointer',
    fontSize: 12, padding: '3px 8px',
  },
  ayuda: {
    margin: `${space.md}px 0 0`, fontSize: text.sec, color: colors.textSec, lineHeight: 1.6,
  },
  avisoInline: {
    margin: `${space.md}px 0 0`, fontSize: text.sec, color: colors.warn,
    background: colors.warnBg, padding: space.lg, borderRadius: radius.sm, lineHeight: 1.6,
  },
  error: {
    background: colors.errBg, color: colors.err, padding: space.lg,
    borderRadius: radius.sm, fontSize: text.body, marginBottom: space.lg,
  },
}
